import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';

const LEAFLET_CSS = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
const LEAFLET_JS = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';

const PropertyMap = ({ property, className = '' }) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const scriptLoadingRef = useRef(false);
  const propertyCoordsRef = useRef(null);

  useEffect(() => {
    if (!property?.coordinates?.lat || !property?.coordinates?.lng) {
      return;
    }

    const { lat, lng } = property.coordinates;
    const popupText = property.title || property.address || 'Property location';
    propertyCoordsRef.current = { lat, lng, popupText };

    const initMap = () => {
      if (!window.L || !mapRef.current || mapInstanceRef.current) return;
      const coords = propertyCoordsRef.current;
      if (!coords || mapRef.current._leaflet_id) return;

      const map = window.L.map(mapRef.current).setView([coords.lat, coords.lng], 15);
      window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap contributors',
        maxZoom: 19
      }).addTo(map);
      window.L.marker([coords.lat, coords.lng])
        .addTo(map)
        .bindPopup(coords.popupText)
        .openPopup();
      mapInstanceRef.current = map;
    };

    if (window.L) {
      initMap();
    } else if (!scriptLoadingRef.current) {
      scriptLoadingRef.current = true;
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = LEAFLET_CSS;
      link.integrity = 'sha256-p4NxAoJBhIIN+hmNHrzRCf9tD/miZyoHS5obTRR9BMY=';
      link.crossOrigin = '';
      document.head.appendChild(link);

      const script = document.createElement('script');
      script.src = LEAFLET_JS;
      script.integrity = 'sha256-20nQCchB9co0qIjJZRGuk2/Z9VM+kNiyxNV1lvTlZBo=';
      script.crossOrigin = '';
      script.onload = initMap;
      document.body.appendChild(script);
    }

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, [property?.coordinates, property?.title, property?.address]);

  // Key forces a fresh container per property so we never reuse a Leaflet-initialized div
  const mapKey = property?.id ?? property?.address ?? 'map';

  if (!property?.coordinates?.lat || !property?.coordinates?.lng) {
    return (
      <div className={`bg-gray-100 rounded-lg flex items-center justify-center ${className}`} style={{ minHeight: '300px' }}>
        <p className="text-gray-500 text-sm">Map location not available</p>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      className={`rounded-lg overflow-hidden border border-gray-200 ${className}`}
      style={{ minHeight: '300px' }}
    >
      <div key={mapKey} ref={mapRef} style={{ width: '100%', height: '100%', minHeight: '300px' }} />
    </motion.div>
  );
};

export default PropertyMap;

